import React, { useRef } from 'react';
import { Line } from 'react-chartjs-2';
import { useSatisfactionOverallTrend } from '../../hooks/useSatisfactionQueries';
import { useTheme } from '../../contexts/ThemeContext';
import { ChartControls } from '../ui';
import useChartGuide from '../../hooks/useChartGuide';
import { useFavoriteToggle } from '../../hooks/useFavoriteToggle';
import { CHART_GUIDES } from '../../constants/chartGuides';
import { getChartColors, getChartBackgroundColor, getChartJsOptions, formatTooltipLabel, mergeChartOptions } from '../../utils/themeHelpers';
import { TYPOGRAPHY_CONFIG } from '../../config/typography.config';
import { TimePeriod } from '../../types/api';

interface OverallSatisfactionProps {
  branchId?: number;
  timeRange?: TimePeriod;
  onError?: (error: Error) => void;
  showLoading?: boolean;
  compact?: boolean;
}

const OverallSatisfaction: React.FC<OverallSatisfactionProps> = ({
  branchId = 0,
  timeRange,
  onError,
  showLoading = true,
  compact = false
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const { currentTheme } = useTheme();
  const { openGuide } = useChartGuide();
  const { handleFavoriteToggle, isFavorited } = useFavoriteToggle();
  const { data, isLoading, error } = useSatisfactionOverallTrend(branchId, timeRange);
  
  // Component metadata
  const componentId = 'overall-satisfaction';
  const componentTitle = 'نمودار روند رضایت مشتریان';

  // Handle error
  React.useEffect(() => {
    if (error && onError) {
      onError(error as Error);
    }
  }, [error, onError]);

  // Helper function to get emoji based on satisfaction percentage
  const getSatisfactionEmoji = (percentage: number): string => {
    if (percentage >= 90) return '😍';  // Excellent
    if (percentage >= 80) return '😊';  // Very Good
    if (percentage >= 70) return '🙂';  // Good
    if (percentage >= 60) return '😐';  // Neutral
    if (percentage >= 50) return '😕';  // Below Average
    if (percentage >= 40) return '☹️';   // Poor
    return '😢';  // Very Poor
  };

  // Use theme-aware colors
  const chartColors = getChartColors(currentTheme);

  // Prepare chart data
  const prepareChartData = () => {
    if (!data || !data.length) return { labels: [], datasets: [] };

    const labels = data.map((item: any) => item.month);
    const satisfactionData = data.map((item: any) =>
      item.satisfaction_percentage != null ? item.satisfaction_percentage : null
    );

    return {
      labels,
      datasets: [{
        label: 'میانگین رضایت کلی',
        data: satisfactionData,
        spanGaps: false,
        borderColor: chartColors[2],
        backgroundColor: chartColors[2].startsWith('#') ? 
          `rgba(${parseInt(chartColors[2].slice(1,3),16)}, ${parseInt(chartColors[2].slice(3,5),16)}, ${parseInt(chartColors[2].slice(5,7),16)}, 0.2)` :
          getChartBackgroundColor(2, currentTheme, 0.2),
        borderWidth: 3,
        pointBackgroundColor: chartColors[2],
        pointBorderColor: currentTheme === 'light' ? '#FFFFFF' : '#27272A',
        pointRadius: 6,
        pointHoverRadius: 10,
        tension: 0.3,
        fill: false
      }]
    };
  };

  // Calculate dynamic Y-axis range
  const calculateDynamicYRange = (datasets: any[]) => {
    if (!datasets.length) return { min: 0, max: 105 };
    
    const values = datasets.flatMap(dataset => 
      dataset.data.filter((value: any) => value !== null && value !== undefined)
    );
    
    if (values.length === 0) return { min: 0, max: 105 };
    
    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);
    const range = maxValue - minValue;
    
    // If values are close together (range < 20), create a tighter scale
    if (range < 20 && minValue > 20) {
      const padding = Math.max(5, range * 0.2);
      return {
        min: Math.max(0, Math.floor(minValue - padding)),
        max: Math.min(100, Math.ceil(maxValue + padding))
      };
    }
    
    // Default wide range for diverse values
    return { min: 0, max: 105 };
  };

  const chartData = prepareChartData();
  const yAxisRange = calculateDynamicYRange(chartData.datasets);

  const chartOptions = mergeChartOptions(getChartJsOptions(currentTheme), {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: !compact
      },
      tooltip: {
        ...getChartJsOptions(currentTheme).plugins.tooltip,
        callbacks: {
          label: (context: any) => {
            if (context.parsed.y === null) return '';
            
            const label = context.dataset.label || '';
            const percentage = context.parsed.y;
            const value = percentage.toFixed(1) + '%';
            const emoji = getSatisfactionEmoji(percentage);
            
            return formatTooltipLabel(label, value);
          }
        }
      }
    },
    scales: {
      ...getChartJsOptions(currentTheme).scales,
      y: {
        ...getChartJsOptions(currentTheme).scales?.y,
        min: yAxisRange.min,
        max: yAxisRange.max,
        title: {
          display: !compact,
          text: 'درصد رضایتمندی',
          color: currentTheme === 'light' ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.95)',
          font: {
            family: 'var(--font-primary)',
            size: TYPOGRAPHY_CONFIG.components.chart.axisLabel.size,
            weight: TYPOGRAPHY_CONFIG.components.chart.axisLabel.weight,
          }
        },
        ticks: {
          ...getChartJsOptions(currentTheme).scales?.y?.ticks,
          callback: (value: any) => { return value + '%'; },
          stepSize: yAxisRange && (yAxisRange.max - yAxisRange.min) < 30 ? 5 : 10,
        }
      },
      x: {
        ...getChartJsOptions(currentTheme).scales?.x,
        title: {
          display: !compact,
          text: 'ماه',
          color: currentTheme === 'light' ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.95)',
          font: {
            family: 'var(--font-primary)',
            size: TYPOGRAPHY_CONFIG.components.chart.axisLabel.size,
            weight: TYPOGRAPHY_CONFIG.components.chart.axisLabel.weight,
          }
        },
      }
    },
    elements: (getChartJsOptions(currentTheme) as any).plugins?.elements || {
      point: {
        radius: 0,
        hoverRadius: 8,
        hoverBorderWidth: 3,
        borderWidth: 2,
      },
      line: {
        tension: 0.4,
        borderCapStyle: 'round' as const,
        borderJoinStyle: 'round' as const,
      },
    }
  });

  // Handle loading state
  if (isLoading && showLoading) {
    return (
      <div className="card-base card-padding animate-enter animate-delay-3">
        <div className="animate-pulse">
          <div className="h-6 bg-bg-secondary rounded w-1/3 mb-4"></div>
          <div className="h-80 bg-bg-secondary rounded"></div>
        </div>
      </div>
    );
  }

  // Handle error state
  if (error) {
    return (
      <div className="card-base card-padding animate-enter animate-delay-3">
        <div className="text-center text-status-error">
          <p>بارگذاری داده‌های رضایت ناموفق بود؛ لطفاً دوباره تلاش کنید.</p>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div ref={chartRef} className="card-base card-padding animate-enter animate-delay-3">
        {!compact && (
          <ChartControls
            componentId={componentId}
            componentTitle={componentTitle}
            componentType="chart"
            dashboardSource="satisfaction"
            onInfo={() => openGuide(CHART_GUIDES.overallSatisfaction)}
            onFavorite={() => handleFavoriteToggle({
              componentId,
              componentTitle,
              dashboardSource: 'satisfaction',
              componentType: 'chart',
              description: 'نمودار خطی روند رضایت مشتریان در طول زمان',
              props: { branchId, timeRange, onError, showLoading, compact },
              gridLayout: {
                containerClass: 'card-base card-padding'
              }
            })}
            isFavorited={isFavorited(componentId)}
            className="mb-4"
            chartRef={chartRef}
          />
        )}
        <h3 className="title-card">{componentTitle}</h3>
        <div className={`flex items-center justify-center ${compact ? 'h-60' : 'h-80'} text-text-secondary`}>
          داده‌ای برای نمایش وجود ندارد
        </div>
      </div>
    );
  }

  return (
    <>
      <div ref={chartRef} className="card-base card-padding animate-enter animate-delay-3">
        {!compact && (
          <ChartControls
            componentId={componentId}
            componentTitle={componentTitle}
            componentType="chart"
            dashboardSource="satisfaction"
            onInfo={() => openGuide(CHART_GUIDES.overallSatisfaction)}
            onFavorite={() => handleFavoriteToggle({
              componentId,
              componentTitle,
              dashboardSource: 'satisfaction',
              componentType: 'chart',
              description: 'نمودار خطی روند رضایت مشتریان در طول زمان',
              props: { branchId, timeRange, onError, showLoading, compact },
              gridLayout: {
                containerClass: 'card-base card-padding'
              }
            })}
            isFavorited={isFavorited(componentId)}
            className="mb-4"
            chartRef={chartRef}
          />
        )}
        <h3 className="title-card">{componentTitle}</h3>
        <div className={compact ? "h-60" : "h-80"}>
          <Line 
            data={chartData} 
            options={chartOptions} 
            height={compact ? 240 : 300}
          />
        </div>
      </div>
    </>
  );
};

export default OverallSatisfaction;